import { Icon } from "@mui/material";
import DeleteDialog from "components/Dialogs/DeleteDielog";
import useAPI from "hooks/useAPI";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { join } from "../../helpers/functions";
import Table from "../Table/Table";
import scss from "./DataTable.module.scss";
import { useTranslation } from "react-i18next";

export const ActionField = ({ value, actionHandlers }) => {
    const actions = value.split("_").filter((action) => action !== "preview");
    const getLabel = (action) => {
        switch (action) {
            case "edit":
                return "edit";
            case "delete":
                return "delete";
            case "list":
                return "list_alt";
            default:
                return "";
        }
    };

    return (
        <div className={scss.actionField}>
            {actions.map((action) => {
                return (
                    <Icon
                        key={action}
                        onClick={(e) => {
                            e.stopPropagation();
                            actionHandlers[action]();
                        }}
                        className={scss.actionIcon}
                    >
                        {getLabel(action)}
                    </Icon>
                );
            })}
        </div>
    );
};

const DataTable = ({ columns, data, apiUrl, maxHeight, actions, navigationKey = ["id"], translateKey, mobileCards = false, previewLabel }) => {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const api = useAPI();
    const [openDeleteDialog, setOpenDeleteDialog] = useState({ show: false, id: null, mutate: null });
    const handleDeleteConfirm = async () => {
        const refetchAction = columns.find((col) => col.type === "edit_delete")?.refetch;
        api.delete(`${apiUrl}/${openDeleteDialog.id}`)
            .then(() => {
                toast.success("Zapis je uspešno obrisan");
                if (refetchAction) refetchAction();
            })
            .catch(() => toast.warning("Došlo je do greške prilikom brisanja"));

        setOpenDeleteDialog({ show: false, id: null, mutate: 1 });
    };

    // Get the proper class name for the cell
    const getCellClassName = (column) => join([column.align, !column.showOnMobile && "mobile-hidden"]);
    const visibleColumns = (columns ?? []).filter((column) => previewLabel || column.field !== "action" || column.type !== "preview");
    const hasPreviewAction = (columns ?? []).some((column) => column.field === "action" && column.type?.split("_").includes("preview"));
    const cardColumns = visibleColumns.filter((column) => column.field !== "action");
    const previewItem = (item) => navigate(`${actions?.previewUrl ?? ""}${navigationKey.map((key) => item[key]).join("/")}`);

    return (
        <>
            <div className={mobileCards ? scss.desktopTable : undefined}>
            <Table maxHeight={maxHeight}>
                <thead>
                    <tr>
                        {visibleColumns.map((column, index) => (
                            <th key={index} className={getCellClassName(column)}>
                                {t(column.label)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(data ?? []).map((item, index) => {
                        const actionHandlers = {
                            edit: () => {
                                navigate(`${item.id}`);
                            },
                            preview: () => {
                                let actionUrl = actions?.previewUrl ?? "";
                                navigate(`${actionUrl}${navigationKey.map((key) => item[key]).join("/")}`);
                                // navigate(`${actionUrl}${item[navigationKey]}`);
                            },
                            delete: () => {
                                setOpenDeleteDialog({ show: true, id: item.id, mutate: null });
                            },
                            modal: () => {
                                setOpenDeleteDialog({ show: true, id: item.id, mutate: null });
                            },
                            navigateToDetailPage: () => {},
                        };

                        return (
                            <tr key={index} onClick={hasPreviewAction ? actionHandlers.preview : undefined} style={hasPreviewAction ? { cursor: "pointer" } : undefined}>
                                {visibleColumns.map((column, index) => {
                                    return (
                                        <td key={index} className={getCellClassName(column)}>
                                            {column.field !== "action" ? item[column.field] : column.type === "preview" && previewLabel ? (
                                                <button type="button" className={scss.previewButton} onClick={(event) => { event.stopPropagation(); actionHandlers.preview(); }}>
                                                    {previewLabel}<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m7.5 5 5 5-5 5" /></svg>
                                                </button>
                                            ) : <ActionField value={column.type} actionHandlers={actionHandlers} />}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
            </div>

            {mobileCards && (
                <div className={scss.mobileCardList}>
                    {(data ?? []).map((item, index) => {
                        const Card = hasPreviewAction ? "button" : "article";
                        return (
                            <Card key={item.id ?? index} type={hasPreviewAction ? "button" : undefined} className={scss.mobileCard} onClick={hasPreviewAction ? () => previewItem(item) : undefined}>
                                {cardColumns.map((column) => (
                                    <div className={scss.mobileCardRow} key={column.field}>
                                        <span>{t(column.label)}</span>
                                        <strong>{item[column.field] ?? "—"}</strong>
                                    </div>
                                ))}
                                {hasPreviewAction && (
                                    <span className={scss.mobileCardOpen}>
                                        Otvori detalje
                                        <span className={scss.mobileCardArrow} aria-hidden="true">
                                            <svg viewBox="0 0 20 20" fill="none"><path d="m7.5 4.5 5.5 5.5-5.5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </span>
                                    </span>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}

            <DeleteDialog
                handleConfirm={handleDeleteConfirm}
                openDeleteDialog={openDeleteDialog}
                setOpenDeleteDialog={setOpenDeleteDialog}
                title={translateKey ? `${translateKey}-delete_dialog-title` : ""}
                // description={translateKey ? `${translateKey}-delete_dialog-description` : ""}
            />
        </>
    );
};

export default DataTable;
